import { useState } from "react";
import { Bell, Mail, Smartphone } from "lucide-react";
import { RazorCheckBox, RazorRadioCheck } from "../../../../components/razor-reuse";
import ReuseModal from "../../../../components/fragment/modal/ReuseModal";
import { plainTextToHtml, stripHtml } from "../../../../utils/Helper";
import {
  CHANNEL_META,
  ChannelSelectionForm,
  isEmailOnlyChannelSelection,
  NotificationChannelType,
  NotificationRecipientType,
  RECIPIENT_TYPE_META,
} from "../types";
import WizardActionBar from "./WizardActionBar";

const CHANNEL_ORDER: NotificationChannelType[] = ["email", "push", "in_app"];
const RECIPIENT_TYPE_ORDER: NotificationRecipientType[] = ["mobile", "business", "both"];
const DEFAULT_RECIPIENT_TYPE: NotificationRecipientType = "both";

const CHANNEL_ICON: Record<NotificationChannelType, React.ReactNode> = {
  email: <Mail className="size-[20px] text-[#734792]" />,
  push: <Bell className="size-[20px] text-[#734792]" />,
  in_app: <Smartphone className="size-[20px] text-[#734792]" />,
};

interface ChannelStepProps {
  channels: ChannelSelectionForm[];
  body: string;
  onChannelsChange: (channels: ChannelSelectionForm[], body: string) => void;
  onCancel: () => void;
  onSaveDraft: () => void;
  onContinue: () => void;
}

export default function ChannelStep({
  channels,
  body,
  onChannelsChange,
  onCancel,
  onSaveDraft,
  onContinue,
}: ChannelStepProps) {
  const [pendingChannels, setPendingChannels] = useState<ChannelSelectionForm[] | null>(null);

  const applyChannelsChange = (nextChannels: ChannelSelectionForm[]) => {
    const wasEmailOnly = isEmailOnlyChannelSelection(channels);
    const willBeEmailOnly = isEmailOnlyChannelSelection(nextChannels);

    if (wasEmailOnly && !willBeEmailOnly) {
      // Leaving Email-only (rich text) mode — body currently holds HTML that
      // won't survive as a plain-text message, so warn before clearing it.
      if (stripHtml(body).trim().length > 0) {
        setPendingChannels(nextChannels);
        return;
      }
      onChannelsChange(nextChannels, "");
      return;
    }

    if (!wasEmailOnly && willBeEmailOnly) {
      // Entering Email-only mode — carry the plain text over, escaped so it
      // renders literally in the rich text editor rather than as markup.
      onChannelsChange(nextChannels, plainTextToHtml(body));
      return;
    }

    onChannelsChange(nextChannels, body);
  };

  const toggleChannel = (channel: NotificationChannelType) => {
    const isSelected = channels.some((c) => c.channel === channel);
    applyChannelsChange(
      isSelected
        ? channels.filter((c) => c.channel !== channel)
        : [...channels, { channel, recipient_type: DEFAULT_RECIPIENT_TYPE }],
    );
  };

  const setRecipientType = (
    channel: NotificationChannelType,
    recipient_type: NotificationRecipientType,
  ) => {
    onChannelsChange(
      channels.map((c) => (c.channel === channel ? { ...c, recipient_type } : c)),
      body,
    );
  };

  const confirmClearBody = () => {
    if (pendingChannels) onChannelsChange(pendingChannels, "");
    setPendingChannels(null);
  };

  const cancelClearBody = () => setPendingChannels(null);

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[24px] mt-[16px]">
      <p className="text-[12px] font-semibold tracking-wide text-[#734792] uppercase">
        Step 1 of 4
      </p>
      <p className="text-[20px] font-bold text-[#1E1E1E] mt-[4px]">
        Select a delivery channel
      </p>

      <div className="space-y-[12px] mt-[24px]">
        {CHANNEL_ORDER.map((channel, index) => {
          const selection = channels.find((c) => c.channel === channel);
          const isSelected = !!selection;
          return (
            <div
              key={channel}
              className={`rounded-[8px] border p-[16px] transition-colors ${
                isSelected ? "border-[#734792] bg-[#F9F6FC]" : "border-[#E4E4E7]"
              }`}
            >
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-[12px]">
                  <div className="flex items-center justify-center size-[40px] rounded-[8px] bg-[#F5F0FA]">
                    {CHANNEL_ICON[channel]}
                  </div>
                  <p className="text-[15px] font-bold text-[#1E1E1E]">
                    {CHANNEL_META[channel].label}
                  </p>
                </div>
                <RazorCheckBox
                  id={index}
                  color="black-light"
                  checked={isSelected}
                  onChange={() => toggleChannel(channel)}
                />
              </label>

              {isSelected && (
                <div className="flex items-center gap-[20px] mt-[16px] pt-[16px] border-t border-[#E4E4E7] pl-[52px]">
                  {RECIPIENT_TYPE_ORDER.map((recipientType, recipientIndex) => (
                    <label
                      key={recipientType}
                      className="flex items-center gap-[8px] cursor-pointer"
                    >
                      <RazorRadioCheck
                        id={index * 10 + recipientIndex}
                        color="black-light"
                        checked={selection?.recipient_type === recipientType}
                        onChange={() => setRecipientType(channel, recipientType)}
                      />
                      <span className="text-[13px] text-[#1E1E1E]">
                        {RECIPIENT_TYPE_META[recipientType]}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <WizardActionBar
        onCancel={onCancel}
        onSaveDraft={onSaveDraft}
        onPrimary={onContinue}
        primaryLabel="Continue"
        primaryDisabled={channels.length < 1}
      />

      <ReuseModal
        title="Clear message body?"
        visible={!!pendingChannels}
        onClose={cancelClearBody}
        onCancel={cancelClearBody}
        onBtnClick={confirmClearBody}
        btnLabel="Clear and continue"
        deleteType
        deleteText="Switching away from Email-only removes rich text formatting, so your current message body will be cleared and can't be recovered."
      />
    </div>
  );
}
